import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { wrapRequest } from 'helpers/requests';

import {
  CUSTOMER_SUPPORT_TICKET_STATUS_TO_COLOR,
  CUSTOMER_SUPPORT_TICKET_STATUS_TO_DOT_COLOR,
  Layout,
  RequestState,
  StatusDisplay,
  Text
} from 'ds';
import ClickableDiv from 'ds/ClickableDiv';
import FeedbackThumbControls from 'ds/FeedbackRequests/FeedbackThumbControls';
import { CUSTOMER_SUPPORT_PATH } from 'routes';
import { titleize } from 'shared';
import { actions as dashboardActions } from 'store/Dashboard';
import { selectDashboardWidgetConfigs } from 'store/Dashboard/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import { addSupportTicketFeedback } from 'ux/Customer/Support/requests';

import { SupportTicketWidgetData } from './types';

interface Props {
  data: SupportTicketWidgetData;
}

const SupportTicketWidget: React.FC<Props> = ({ data, data: { status, title, id } }) => {
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const history = useHistory();

  const onVisibleChange = (visible: boolean) => {
    if (!visible) {
      setTimeout(() => {
        setModalIsVisible(visible);
      }, 150);
    } else {
      setModalIsVisible(visible);
    }
  };

  return (
    // <hack> ClickableDiv and storing visiblity state are used here to prevent
    // navigation to the support ticket link. I tried with <Link>, but it was not working.
    <ClickableDiv
      onClick={() => {
        if (modalIsVisible) return;
        history.push(`${CUSTOMER_SUPPORT_PATH}/${id}`);
      }}
      style={{ width: '100%', height: '100%' }}
    >
      <Layout
        direction="column"
        gap={8}
        paddingX={12}
        paddingY={16}
        color="gray-25"
        borderRadius={16}
        height="100%"
        width="100%"
      >
        <Layout align="center" justify="space-between" gap={12}>
          <Text size="body-xs" ellipsis>
            {title}
          </Text>
          <Layout flexShrink={0}>
            <StatusDisplay
              color={CUSTOMER_SUPPORT_TICKET_STATUS_TO_COLOR[status]}
              dotColor={CUSTOMER_SUPPORT_TICKET_STATUS_TO_DOT_COLOR[status]}
              textColor={CUSTOMER_SUPPORT_TICKET_STATUS_TO_DOT_COLOR[status]}
              text={titleize(status)}
              includeCaret={false}
              size="sm"
            />
          </Layout>
        </Layout>
        <BottomSection data={data} onVisibleChange={onVisibleChange} />
      </Layout>
    </ClickableDiv>
  );
};

const BottomSection: React.FC<Props & { onVisibleChange: (visible: boolean) => void }> = ({
  data: { id, num_new_messages, status, feedback },
  onVisibleChange
}) => {
  const dispatch = useAppDispatch();
  const [requestState, setRequestState] = useState<RequestState>('ready');
  const widgetConfigs = useAppSelector(selectDashboardWidgetConfigs);
  const tenancyId = useAppSelector(selectTenancyId);

  const handleFeedback = async ({ liked, message }: { liked: boolean; message?: string }) => {
    if (!id || !tenancyId || !widgetConfigs) return;

    setRequestState('in_progress');
    await wrapRequest(
      addSupportTicketFeedback({
        supportTicketId: id,
        liked,
        message
      }),
      { successMessage: 'Feedback submitted.' }
    );

    const updatedWidgetConfigs = widgetConfigs.map(row =>
      row.map(config => {
        if (config.type === 'support_ticket' && config.data?.id === id) {
          return {
            ...config,
            data: {
              ...config.data,
              feedback: { liked }
            }
          };
        }
        return config;
      })
    );
    dispatch(dashboardActions.setWidgetConfigs(updatedWidgetConfigs));

    // setTimeout(async () => {
    //   const widgetConfigs = await getDashboardWidgetConfigs({ tenancyId });
    //   dispatch(dashboardActions.setWidgetConfigs(widgetConfigs));
    // }, 1000);
  };

  if (status === 'closed') {
    return (
      <Layout gap={4} align="center">
        <Text size="body-xs" color="gray-400">
          Was your issue resolved?
        </Text>
        <FeedbackThumbControls
          liked={feedback?.liked}
          disabled={requestState === 'in_progress'}
          handleFeedback={handleFeedback}
          onVisibleChange={onVisibleChange}
        />
      </Layout>
    );
  }

  return (
    <Layout gap={8} align="center">
      {num_new_messages > 0 ? (
        <Text size="body-xs" color="gray-400">
          {num_new_messages} new comments
        </Text>
      ) : null}
    </Layout>
  );
};

export default SupportTicketWidget;
